import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  SelectChangeEvent,
  Autocomplete,
  TextField,
  Chip,
  Stack
} from '@mui/material';
import { DataSource, ShowAs, GroupBy, Entity, ALL_OPTION } from '../hooks/useChartData';
import SelectField from '../../NewUI/Components/CustomUIElements/SelectField';
import { sharedClasses } from '../../NewUI/Components/CustomUIElements/sharedClasses';

interface ChartFiltersProps {
  dataSource: DataSource | '';
  showAs: ShowAs | '';
  groupBy: GroupBy | '';
  selectedEntities: Entity[];
  entities: Entity[];
  onDataSourceChange: (event: SelectChangeEvent<DataSource>) => void;
  onShowAsChange: (event: SelectChangeEvent<ShowAs>) => void;
  onGroupByChange: (event: SelectChangeEvent<GroupBy>) => void;
  onEntitiesChange: (_: React.SyntheticEvent, newValue: Entity[]) => void;
  getShowAsOptions: () => { value: ShowAs; label: string }[];
  getGroupByOptions: () => { value: GroupBy; label: string }[];
}

export const ChartFilters: React.FC<ChartFiltersProps> = ({
  dataSource,
  showAs,
  groupBy,
  selectedEntities,
  entities,
  onDataSourceChange,
  onShowAsChange,
  onGroupByChange,
  onEntitiesChange,
  getShowAsOptions,
  getGroupByOptions
}) => {
  const handleEntitiesChange = (_: React.SyntheticEvent, newValue: Entity[]) => {
    if (newValue.length === 0) {
      // If nothing is selected, default to "All"
      onEntitiesChange(_, [ALL_OPTION]);
      return;
    }

    const lastSelected = newValue[newValue.length - 1];
    const hasAll = lastSelected.id === 0;
    const hasSpecific = newValue.some((e) => e.id !== 0);

    if (hasAll) {
      // If "All" is selected, clear other selections
      onEntitiesChange(_, [ALL_OPTION]);
    } else if (hasSpecific) {
      // If specific entities are selected, remove "All" option
      onEntitiesChange(
        _,
        newValue.filter((e) => e.id !== 0)
      );
    }
  };

  const availableOptions = React.useMemo(() => {
    const hasSpecific = selectedEntities.some((e) => e.id !== 0);
    return hasSpecific ? entities : [ALL_OPTION, ...entities];
  }, [entities, selectedEntities]);

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
      <FormControl>
        <SelectField
          id="data-source-select"
          label="Data Source"
          options={[
            { value: 'jobs', label: 'Jobs' },
            { value: 'applications', label: 'Applications' }
          ]}
          value={dataSource}
          onChange={(value) =>
            onDataSourceChange({ target: { value } } as SelectChangeEvent<DataSource>)
          }
          styles={{ width: '200px' }}
        />
      </FormControl>

      {dataSource && (
        <FormControl>
          <SelectField
            id="show-as-select"
            label="Show As"
            options={getShowAsOptions()}
            value={showAs}
            onChange={(value) => onShowAsChange({ target: { value } } as SelectChangeEvent<ShowAs>)}
            styles={{ width: '200px' }}
          />
        </FormControl>
      )}

      {showAs && (
        <FormControl>
          <SelectField
            id="group-by-select"
            label="Group By"
            options={getGroupByOptions()}
            value={groupBy}
            onChange={(value) =>
              onGroupByChange({ target: { value } } as SelectChangeEvent<GroupBy>)
            }
            styles={{ width: '200px' }}
          />
        </FormControl>
      )}

      {dataSource && showAs && groupBy && (
        <Autocomplete
          disablePortal
          disableCloseOnSelect
          multiple
          getOptionLabel={(option) => option.name}
          blurOnSelect={false}
          options={availableOptions}
          value={selectedEntities}
          sx={{ ...sharedClasses.formAutocomplete, width: '300px', marginTop: 3 }}
          loading={entities.length === 0}
          loadingText="Loading entities..."
          noOptionsText="No entities found"
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options, { inputValue }) => {
            const searchTerm = inputValue.toLowerCase();
            return options.filter((option) => option.name.toLowerCase().includes(searchTerm));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Entities"
              placeholder="Please select"
              sx={sharedClasses.formAutocomplete}
            />
          )}
          onChange={(event, value) => handleEntitiesChange(event, value)}
        />
      )}
    </Box>
  );
};
